import { OrderedMap, List } from "immutable";
import moment from "moment-timezone";
import { createSelector } from "reselect";
import {
  PROJECT_TEMPLATE_DISCUSSION,
  PROJECT_TEMPLATE_NOTE,
  PROJECT_TEMPLATE_TASK,
} from "~/pages/ProjectTemplates/utils.js";
import { descSortCompare } from "~/utils/utils.jsx";
import { createImmutableIsEqualSelector } from "./index.jsx";
import { getLoggedUser } from "./selector.users.jsx";

export const getAttachmentById = (state, id) => state.attachments.get(id.toString());
export const getAllAttachments = (state) => state.attachments;
export const getNormalAttachments = (state) =>
  getAllAttachments(state).filter((a) => {
    return a.disposition === `attachment`;
  });
export const getInlineAttachments = (state) =>
  getAllAttachments(state).filter((a) => {
    return a.disposition === `inline`;
  });

const getAttachmentsByIds = (state, ids = []) => {
  return getAllAttachments(state).filter((attachment) => {
    return ids.indexOf(attachment.id) > -1;
  });
};

export const getNormalAttachmentsByIds = createSelector(
  getNormalAttachments,
  (state, ids = []) => ids,
  (attachments, ids) => {
    return attachments.filter((att) => ids.indexOf(att.id) > -1);
  }
);

export const getAttachmentsByIdAndParentType = (state, parent_type, parent_id) => {
  let parent = null;

  switch (parent_type) {
    case `Task`:
      parent = state.tasks.get(`${parent_id}`);
      break;
    case PROJECT_TEMPLATE_TASK:
      parent = state.project_template_elements[parent_id];
      break;
    case `Discussion`:
      parent = state.discussions[parent_id];
      break;
    case PROJECT_TEMPLATE_DISCUSSION:
      parent = state.project_template_elements[parent_id];
      break;
    case `Expense`:
      parent = state.expenses[parent_id];
      break;
    case `Note`:
      parent = state.notes[parent_id];
      break;
    case PROJECT_TEMPLATE_NOTE:
      parent = state.project_template_elements[parent_id];
      break;
    case `Comment`:
      parent = state.comments.get(`${parent_id}`);
      break;
    default:
      return;
  }

  return parent ? getNormalAttachmentsByIds(state, parent.attachments) : OrderedMap();
};

export const makeAttachmentsByIdsSelector = () => {
  return createImmutableIsEqualSelector(getAttachmentsByIds, (attachments) => attachments);
};

const getAttachmentIdsByParent = (state, { parentType, parentId }) => {
  let parent = null;

  switch (parentType) {
    case `Task`:
      parent = state?.tasks?.get(`${parentId}`);
      break;
    case PROJECT_TEMPLATE_TASK:
      if (state.project_template_elements && state.project_template_elements[parentId]) {
        return state.project_template_elements[parentId].attachments;
      }
      break;
    case `Discussion`:
      if (state.discussions && state.discussions[parentId]) {
        return state.discussions[parentId].attachments;
      }
      break;
    case PROJECT_TEMPLATE_DISCUSSION:
      if (state.project_template_elements && state.project_template_elements[parentId]) {
        return state.project_template_elements[parentId].attachments;
      }
      break;
    case `Note`:
      if (state.notes && state.notes[parentId]) {
        return state.notes[parentId].attachments;
      }
      break;
    case PROJECT_TEMPLATE_NOTE:
      if (state.project_template_elements && state.project_template_elements[parentId]) {
        return state.project_template_elements[parentId].attachments;
      }
      break;
    case `Expense`:
      if (state.expenses && state.expenses[parentId]) {
        return state.expenses[parentId].attachments;
      }
      break;
    case `Comment`:
      parent = state?.comments?.get(`${parentId}`);
      break;
    case "ActiveCollab\\Module\\System\\Model\\Message\\UserMessage":
      parent = state?.conversation_messages?.get(`${parentId}`);
      break;
    case "CalendarEvent": {
      if (state.calendarEvents && state.calendarEvents[`${parentId}`]) {
        parent = state.calendarEvents[parentId];
        return parent.attachments;
      }
      break;
    }
    default:
      break;
  }

  return parent?.get(`attachments`);
};

export const makeGetAllAttachmentsByParent = ({ parentType, parentId }) => {
  return createSelector(
    getAllAttachments,
    (state) => getAttachmentIdsByParent(state, { parentType, parentId }),
    (attachments, attachmentIds = List()) => {
      if (List.isList(attachmentIds)) {
        attachmentIds = attachmentIds.toArray();
      }

      return attachmentIds
        .map((id) => attachments?.get(`${id}`)?.toJS())
        .filter((attachment) => typeof attachment !== "undefined")
        .sort((a, b) => descSortCompare(a.disposition, b.disposition));
    }
  );
};

export const makeGetAttachmentParent = (attachmentId) => {
  return createSelector(
    (state) => state?.tasks,
    (state) => state?.comments,
    (state) => state?.discussions,
    (state) => state?.notes,
    (state) => state?.recurringTasks,
    (state) => state?.projects,
    (state) => state?.expenses,
    (state) => getAttachmentById(state, attachmentId),
    (tasks, comments, discussions, notes, recurringTasks, projects, expenses, attachment) => {
      const { parent_id, parent_type } = attachment;
      let parent, comment, expense;
      switch (parent_type) {
        case "RecurringTask":
          parent = recurringTasks[parent_id];
          break;
        case "Task":
          parent = tasks.get(`${parent_id}`);
          break;
        case "Discussion":
          parent = discussions[parent_id];
          break;
        case "Note":
          parent = notes[parent_id];
          break;
        case "Expense":
          expense = expenses[parent_id];
          if (expense.parent_type === "Project") {
            parent = projects.get(`${expense.parent_id}`);
          } else {
            parent = tasks.get(`${expense.parent_id}`);
          }
          break;
        case "Comment":
          if (comments.get(`${parent_id}`)) {
            comment = comments.get(`${parent_id}`);
          }
          if (comment?.parent_type === `Task`) {
            parent = tasks.get(`${comment.parent_id}`);
          }
          if (comment?.parent_type === `Note`) {
            parent = notes[comment.parent_id];
          }
          if (comment?.parent_type === `Discussion`) {
            parent = discussions[comment.parent_id];
          }
          break;
      }
      return { parent_type, parent };
    }
  );
};

export const getActiveAttachments = createSelector(
  getAllAttachments,
  (state) => state?.tasks,
  (state) => state?.comments,
  (state) => state?.discussions,
  (state) => state?.notes,
  (state) => state?.recurringTasks,
  (state) => state?.projects,
  (state) => state?.expenses,
  (attachments, tasks, comments, discussions, notes, recurringTasks, projects, expenses) => {
    return attachments.filter((attachment) => {
      const { parent_id, parent_type } = attachment;
      let parent, comment, expense;

      switch (parent_type) {
        case "RecurringTask":
          parent = recurringTasks[parent_id];
          break;
        case "Task":
          parent = tasks.get(`${parent_id}`);
          break;
        case "Discussion":
          parent = discussions[parent_id];
          break;
        case "Note":
          parent = notes[parent_id];
          break;
        case "Expense":
          if (expenses[parent_id]) {
            expense = expenses[parent_id];
          }
          if (expense?.trashed_on) return false;
          if (expense?.parent_type === `Task`) {
            parent = tasks.get(`${expense.parent_id}`);
          }
          if (expense?.parent_type === `Project`) {
            parent = projects.get(`${expense.parent_id}`);
          }
          break;
        case "Comment":
          if (comments.get(`${parent_id}`)) {
            comment = comments.get(`${parent_id}`);
          }
          if (comment?.trashed_on) return false;
          if (comment?.parent_type === `Task`) {
            parent = tasks.get(`${comment.parent_id}`);
          }
          if (comment?.parent_type === `Note`) {
            parent = notes[comment.parent_id];
          }
          if (comment?.parent_type === `Discussion`) {
            parent = discussions[comment.parent_id];
          }
          break;
        default:
          break;
      }
      return !parent?.trashed_on;
    });
  }
);

export const makeGetAttachmentPermissions = (attachmentId) => {
  const getAttachmentParent = makeGetAttachmentParent(attachmentId);
  return createSelector(
    (state) => getAttachmentById(state, attachmentId),
    getAttachmentParent,
    getLoggedUser,
    (state) => state?.comments,
    (attachment, { parent_type, parent }, user, comments) => {
      const canManageTasks = user?.custom_permissions.some((value) => value === "can_manage_tasks");

      if (parent_type === "Comment") {
        parent = comments.get(`${attachment.parent_id}`);
      }

      let permissions = {
        create: !!parent,
        read: !!attachment,
        update: !!attachment,
        delete: !!attachment,
      };

      if (user?.class === "Client") {
        permissions.create = false;
        permissions.read = true;
        permissions.update = false;
        permissions.delete = false;
      }

      if (canManageTasks) {
        permissions.create = permissions.update = permissions.delete = true;
      }

      if (user.class !== "Owner" && parent_type === "Comment") {
        permissions.create =
          permissions.update =
          permissions.delete =
            parent.created_by_id === user.id && moment().unix() - parent.created_on <= 1800;
      }

      return permissions;
    }
  );
};
